import { createContext, useContext, type ReactNode } from 'react';

import type { Question, QuestionnaireAnswers } from '@shared';
import type { ResolvedQuestion } from '@/features/advisor-onboarding/questionnaire/hooks/use-resolved-question';

export interface QuestionScreenProgress {
  current: number;
  estimatedTotal: number;
}

export interface QuestionScreenContextValue {
  question: Question;
  resolved: ResolvedQuestion;
  answers: QuestionnaireAnswers;
  progress: QuestionScreenProgress;
  isSubmitting: boolean;
  onBack: () => void;
  submitAnswer: (patch: Partial<QuestionnaireAnswers>) => Promise<void>;
  submitAnswerWithoutAdvance: (patch: Partial<QuestionnaireAnswers>) => Promise<void>;
  submitSkip: () => Promise<void>;
  advance: (patch?: Partial<QuestionnaireAnswers>) => void;
}

const QuestionScreenContext = createContext<QuestionScreenContextValue | null>(null);

interface QuestionScreenProviderProps extends QuestionScreenContextValue {
  children: ReactNode;
}

export function QuestionScreenProvider({ children, ...value }: QuestionScreenProviderProps) {
  return <QuestionScreenContext.Provider value={value}>{children}</QuestionScreenContext.Provider>;
}

export function useQuestionScreen(): QuestionScreenContextValue {
  const ctx = useContext(QuestionScreenContext);

  if (!ctx) {
    throw new Error('useQuestionScreen must be used inside a QuestionScreenProvider');
  }

  return ctx;
}
